import React, { useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { formatJpWithAlias } from '@/utils/partDisplay';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Handshake, IndianRupee, Send, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PriceRequestDialog = ({ 
  isOpen, 
  onClose, 
  part, 
  onRequestSubmitted 
}) => {
  const { token } = useAuth();
  const [targetPrice, setTargetPrice] = useState('');
  const [targetQuantity, setTargetQuantity] = useState('1');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!targetPrice || parseFloat(targetPrice) <= 0) {
      toast.error('Please enter a valid target price');
      return;
    }
    
    if (!targetQuantity || parseInt(targetQuantity) <= 0) {
      toast.error('Please enter a valid quantity');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/price-requests`, {
        jp_part_no: part.jp_part_no,
        part_name: part.part_name || part.name,
        original_price: part.tax_paid_price || part.price,
        requested_price: parseFloat(targetPrice),
        requested_quantity: parseInt(targetQuantity),
        notes: notes || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Price request submitted! We will review and respond soon.');
      onRequestSubmitted?.();
      onClose();
      
      // Reset form
      setTargetPrice('');
      setTargetQuantity('1');
      setNotes('');
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to submit price request';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const originalPrice = part?.tax_paid_price || part?.price || 0;
  const discount = targetPrice ? ((originalPrice - parseFloat(targetPrice)) / originalPrice * 100).toFixed(1) : 0;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md bg-white max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-gray-900">
            <div className="p-2 bg-amber-100 rounded-full">
              <Handshake className="w-5 h-5 text-amber-600" />
            </div>
            Request Special Price
          </DialogTitle>
          <DialogDescription className="text-gray-600">
            Submit your target price for this product. Our team will review and respond.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          {/* Part Info */}
          <div className="bg-gray-50 rounded-lg p-3 border">
            <p className="font-mono text-sm text-blue-600 font-semibold">{formatJpWithAlias(part || {})}</p>
            <p className="text-sm text-gray-700 mt-1 line-clamp-2">{part?.part_name || part?.name}</p>
            <p className="text-lg font-bold text-gray-900 mt-2">
              Current: ₹{originalPrice?.toLocaleString('en-IN')}
              <span className="text-xs text-gray-500 font-normal ml-1">(incl. GST)</span>
            </p>
          </div>

          {/* Target Price */}
          <div>
            <Label className="text-sm font-semibold text-gray-700">
              Your Target Price (incl. GST) *
            </Label>
            <div className="relative mt-1">
              <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                type="number"
                value={targetPrice}
                onChange={(e) => setTargetPrice(e.target.value)}
                placeholder="Enter your target price"
                className="pl-9"
                min="1"
                step="0.01"
              />
            </div>
            {targetPrice && parseFloat(targetPrice) < originalPrice && (
              <p className="text-xs text-green-600 mt-1">
                {discount}% discount requested
              </p>
            )}
            {targetPrice && parseFloat(targetPrice) >= originalPrice && (
              <p className="text-xs text-orange-600 mt-1">
                Target price should be less than current price
              </p>
            )}
          </div>

          {/* Target Quantity */}
          <div>
            <Label className="text-sm font-semibold text-gray-700">
              Expected Quantity *
            </Label>
            <Input
              type="number"
              value={targetQuantity}
              onChange={(e) => setTargetQuantity(e.target.value)}
              placeholder="How many units?"
              className="mt-1"
              min="1"
            />
            <p className="text-xs text-gray-500 mt-1">
              Helps us understand your requirement better
            </p>
          </div>

          {/* Notes */}
          <div>
            <Label className="text-sm font-semibold text-gray-700">
              Additional Notes (Optional)
            </Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Any specific requirements or reasons for this price..."
              className="mt-1"
              rows={2}
            />
          </div>

          {/* Info Box */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-700">
            <p className="font-semibold">How it works:</p>
            <ul className="list-disc list-inside mt-1 space-y-0.5 text-xs">
              <li>We'll review your request within 24 hours</li>
              <li>You'll receive a notification with our response</li>
              <li>If accepted, the special price applies to all your future orders</li>
            </ul>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            disabled={submitting || !targetPrice || parseFloat(targetPrice) >= originalPrice}
            className="bg-amber-600 hover:bg-amber-700"
          >
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Submitting...
              </>
            ) : (
              <>
                <Send className="w-4 h-4 mr-2" />
                Submit Request
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PriceRequestDialog;
